import { useState, useEffect } from "react";

import { useQuery } from "@tanstack/react-query";
import { Plus, GitBranch, Loader2, Copy, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";


import { CreateVersionDialog } from "./CreateVersionDialog";
import { VersionComparisonDialog } from "./VersionComparisonDialog";


const STORAGE_KEY = "prompt-versions-selected";

export const PromptVersions = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showComparisonDialog, setShowComparisonDialog] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<string[]>(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();

  // Save to localStorage whenever selection changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedVersions));
  }, [selectedVersions]);

  const { data: versions, isLoading, refetch } = useQuery({
    queryKey: ["prompt-versions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_versions" as any)
        .select(`
          *,
          prompt_templates:template_id (name)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching versions:", error);
        throw error;
      }
      return data;
    },
    retry: 1,
  });

  const handleCreateSuccess = () => {
    refetch();
    setShowCreateDialog(false);
    setSelectedTemplateId(undefined);
    toast.success("Version created successfully");
  };

  const handleQuickCreate = (templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTemplateId(templateId);
    setShowCreateDialog(true);
  };

  const handleClearSelection = () => {
    setSelectedVersions([]);
    toast.success("Selection cleared");
  };

  const handleVersionSelect = (versionId: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      }
      if (prev.length >= 2) {
        return [prev[1], versionId];
      }
      return [...prev, versionId];
    });
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      setShowComparisonDialog(true);
    }
  };

  const getInitialTemplateId = (): string | undefined => {
    if (selectedVersions.length === 1 && versions && Array.isArray(versions)) {
      const selectedVersion = versions.find((v: any) => v.id === selectedVersions[0]) as any;
      return selectedVersion?.template_id || undefined;
    }
    return undefined;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Prompt Versions</h2>
            <p className="text-muted-foreground">Loading versions...</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="h-5 w-5 bg-muted animate-pulse rounded" />
                  <div className="flex gap-2">
                    <div className="h-6 w-16 bg-muted animate-pulse rounded" />
                    <div className="h-6 w-12 bg-muted animate-pulse rounded" />
                  </div>
                </div>
                <div className="h-6 w-48 bg-muted animate-pulse rounded mt-4" />
                <div className="h-4 w-full bg-muted animate-pulse rounded mt-2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 w-full bg-muted animate-pulse rounded" />
                  <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Prompt Versions</h2>
          <p className="text-muted-foreground">Manage different versions of prompts</p>
        </div>
        <div className="flex gap-2">
          {selectedVersions.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClearSelection}
            >
              <X className="h-4 w-4 mr-2" />
              Clear Selection
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleCompare}
            disabled={selectedVersions.length !== 2}
          >
            Compare Selected
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Version
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {versions?.map((version: any) => (
          <Card
            key={version.id}
            className={`cursor-pointer transition-colors ${
              selectedVersions.includes(version.id) ? "ring-2 ring-primary" : ""
            }`}
            onClick={() => handleVersionSelect(version.id)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <GitBranch className="h-5 w-5 text-primary" />
                <div className="flex gap-2">
                  {version.is_latest_version && <Badge>Latest</Badge>}
                  {version.status === 'active' && <Badge variant="secondary">Active</Badge>}
                  <Badge variant="outline">v{version.version}</Badge>
                </div>
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <CardTitle className="text-lg">
                    {version.prompt_templates?.name || "Unknown Template"}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {typeof version.content === 'object' 
                      ? JSON.stringify(version.content).substring(0, 100)
                      : version.content?.substring(0, 100) || 'No content'
                    }...
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={(e) => handleQuickCreate(version.template_id, e)}
                  title="Create new version from this template"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">
                    {new Date(version.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium capitalize">{version.status}</span>
                </div>
                {version.change_notes && (
                  <div className="mt-2">
                    <span className="text-muted-foreground">Changes:</span>
                    <p className="text-sm mt-1">{version.change_notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {versions?.length === 0 && (
          <div className="col-span-full text-center py-12">
            <GitBranch className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">No versions yet</p>
            <p className="text-muted-foreground">Create your first prompt version</p>
          </div>
        )}
      </div>

      <CreateVersionDialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) setSelectedTemplateId(undefined);
        }}
        onSuccess={handleCreateSuccess}
        initialTemplateId={selectedTemplateId || getInitialTemplateId()}
      />

      <VersionComparisonDialog
        open={showComparisonDialog}
        onOpenChange={setShowComparisonDialog}
        versionIds={selectedVersions}
        onClose={() => {
          setShowComparisonDialog(false);
          setSelectedVersions([]);
        }}
      />
    </div>
  );
};
